import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useHistory } from "react-router-dom";

import AudiotrackOutlinedIcon from "@mui/icons-material/AudiotrackOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DesktopMacOutlinedIcon from "@mui/icons-material/DesktopMacOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import PlaylistPlayOutlinedIcon from "@mui/icons-material/PlaylistPlayOutlined";
import PollOutlinedIcon from "@mui/icons-material/PollOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { getNotificationVisibleDate } from "./notificationTiming";

dayjs.extend(relativeTime);

const notificationTypeDetails = {
    announcement: {
        label: "Announcement",
        icon: CampaignOutlinedIcon,
    },
    audio: {
        label: "Audio",
        icon: AudiotrackOutlinedIcon,
    },
    desktopApp: {
        label: "Desktop App",
        icon: DesktopMacOutlinedIcon,
    },
    file: {
        label: "File",
        icon: InsertDriveFileOutlinedIcon,
    },
    image: {
        label: "Image",
        icon: ImageOutlinedIcon,
    },
    link: {
        label: "Link",
        icon: LinkOutlinedIcon,
    },
    pdf: {
        label: "PDF",
        icon: PictureAsPdfOutlinedIcon,
    },
    playlist: {
        label: "Playlist",
        icon: PlaylistPlayOutlinedIcon,
    },
    project: {
        label: "Project",
        icon: FolderOutlinedIcon,
    },
    survey: {
        label: "Survey",
        icon: PollOutlinedIcon,
    },
    timeline: {
        label: "Timeline",
        icon: TimelineOutlinedIcon,
    },
    video: {
        label: "Video",
        icon: MovieOutlinedIcon,
    },
};

export default function NotificationCard({
    notification,
    popupFeed,
    updateSeenBy,
    type,
    destination,
    mediaUrl,
    mediaAlt,
    mediaFit = "cover",
    mediaContent,
    showComments = false,
}) {
    const history = useHistory();
    const typeDetails =
        notificationTypeDetails[type] || notificationTypeDetails.file;
    const TypeIcon = typeDetails.icon;
    const canOpen = Boolean(destination);
    const notificationDate = getNotificationVisibleDate(notification);
    const relativeDate = notificationDate?.isValid()
        ? notificationDate.fromNow()
        : null;

    function handleOpen() {
        if (!canOpen) return;

        updateSeenBy(notification);
        history.push(destination);
    }

    function handleDismiss(event) {
        event.stopPropagation();
        updateSeenBy(notification);
    }

    const content = (
        <CardContent
            sx={{
                px: { xs: 2, sm: 2.5 },
                py: { xs: 2, sm: 2.25 },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 1.25,
                    pr: popupFeed ? 4 : 0,
                }}
            >
                <Box
                    sx={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        color: "primary.main",
                        backgroundColor: "action.hover",
                    }}
                >
                    <TypeIcon sx={{ fontSize: 18 }} />
                </Box>
                <Typography
                    variant="body2"
                    sx={{
                        color: "text.secondary",
                        fontWeight: 700,
                        lineHeight: 1.2,
                    }}
                >
                    {typeDetails.label}
                </Typography>
                {notification.hubName && (
                    <Chip
                        size="small"
                        label={notification.hubName}
                        sx={{
                            height: 25,
                            backgroundColor: "action.hover",
                            color: "text.secondary",
                            "& .MuiChip-label": {
                                px: 1.1,
                            },
                        }}
                    />
                )}
                {relativeDate && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: "text.secondary",
                            ml: { sm: "auto" },
                        }}
                        title={notificationDate.format("D MMMM YYYY, HH:mm")}
                    >
                        {relativeDate}
                    </Typography>
                )}
            </Box>

            <Typography
                variant="h6"
                component="h2"
                sx={{
                    color: "text.primary",
                    lineHeight: 1.25,
                    mb: notification.message ? 0.75 : 0,
                }}
            >
                {notification.title}
            </Typography>
            {notification.message && (
                <Typography
                    variant="body2"
                    component="p"
                    sx={{
                        color: "text.secondary",
                        lineHeight: 1.6,
                        whiteSpace: "pre-line",
                    }}
                >
                    {notification.message}
                </Typography>
            )}
        </CardContent>
    );

    return (
        <Card
            variant="outlined"
            sx={{
                position: "relative",
                overflow: "hidden",
                borderRadius: 3,
                borderColor: "divider",
                backgroundColor: "background.paper",
                boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                        ? "0 2px 10px rgba(0, 0, 0, 0.24)"
                        : "0 2px 10px rgba(20, 35, 25, 0.045)",
                transition:
                    "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
                "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: (theme) =>
                        theme.palette.mode === "dark"
                            ? "0 7px 20px rgba(0, 0, 0, 0.34)"
                            : "0 7px 20px rgba(20, 35, 25, 0.075)",
                    borderColor: "text.disabled",
                },
            }}
        >
            {popupFeed && (
                <Tooltip title="Dismiss">
                    <IconButton
                        aria-label="Dismiss notification"
                        size="small"
                        onClick={handleDismiss}
                        sx={{
                            position: "absolute",
                            zIndex: 2,
                            top: 12,
                            right: 12,
                            color: "text.secondary",
                            backgroundColor: "background.paper",
                            "&:hover": {
                                backgroundColor: "action.hover",
                                color: "text.primary",
                            },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}

            {mediaUrl && (
                <CardActionArea
                    component="button"
                    onClick={handleOpen}
                    disabled={!canOpen}
                    sx={{ display: "block" }}
                >
                    <CardMedia
                        component="img"
                        image={mediaUrl}
                        alt={mediaAlt || notification.title}
                        sx={{
                            width: "100%",
                            aspectRatio: "16 / 9",
                            objectFit: mediaFit,
                            backgroundColor: "background.media",
                        }}
                    />
                </CardActionArea>
            )}

            {mediaContent && (
                <Box
                    onClick={handleOpen}
                    sx={{
                        p: { xs: 1.5, sm: 2 },
                        pb: 0,
                        backgroundColor: "background.subtle",
                    }}
                >
                    {mediaContent}
                </Box>
            )}

            {canOpen ? (
                <CardActionArea onClick={handleOpen}>
                    {content}
                </CardActionArea>
            ) : (
                content
            )}

            {showComments && (
                <CardActions
                    sx={{
                        px: { xs: 1.25, sm: 1.75 },
                        py: 0.75,
                        borderTop: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <Button
                        size="small"
                        color="primary"
                        startIcon={<ChatBubbleOutlineIcon />}
                        onClick={handleOpen}
                        sx={{ fontWeight: 700 }}
                    >
                        Comments ({notification.comments.length})
                    </Button>
                </CardActions>
            )}
        </Card>
    );
}
